import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output, computed, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../core/services/auth.service';
import {
  FormularioPadraoComponent,
  SecaoFormulario,
} from '../../shared/components/formulario-padrao/formulario-padrao.component';
import { MensagemService } from '../../shared/services/mensagem.service';
import { BotaoVoltarComponent } from '../../shared/components/botao-voltar/botao-voltar.component';
import { BotaoVoltarContextService } from '../../shared/services/botao-voltar-context.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ButtonModule,
    MenuModule,
    DialogModule,
    ReactiveFormsModule,
    FormularioPadraoComponent,
    BotaoVoltarComponent,
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();

  protected menuItens: MenuItem[] = [];
  protected dialogPerfilAberto = false;
  protected dialogSenhaAberto = false;
  protected carregandoPerfil = false;
  protected carregandoSenha = false;

  protected formPerfil: FormGroup;
  protected formSenha: FormGroup;
  protected urlListagemAtual = signal('/');

  protected mostrarBotaoVoltar = computed(
    () => this.botaoVoltarContext.visivel() || this.ehRotaCadastroDetalhe(),
  );
  private readonly ehRotaCadastroDetalhe = signal(false);
  private routerSubscription?: Subscription;

  protected secoesPerfil: SecaoFormulario[] = [
    {
      titulo: 'Meus dados',
      campos: [
        { chave: 'nome', rotulo: 'Nome', tipo: 'texto', placeholder: 'Nome completo', colSpan: 12 },
        {
          chave: 'email',
          rotulo: 'E-mail',
          tipo: 'email',
          placeholder: 'email@fortaris.com',
          colSpan: 12,
        },
      ],
    },
  ];

  protected secoesSenha: SecaoFormulario[] = [
    {
      titulo: 'Alterar senha',
      descricao: 'Defina uma nova senha para acesso.',
      campos: [
        {
          chave: 'senhaAtual',
          rotulo: 'Senha atual',
          tipo: 'senha',
          placeholder: 'Informe a senha atual',
          colSpan: 12,
        },
        {
          chave: 'novaSenha',
          rotulo: 'Nova senha',
          tipo: 'senha',
          placeholder: 'Mínimo de 6 caracteres',
          colSpan: 12,
        },
        {
          chave: 'confirmarSenha',
          rotulo: 'Confirmar senha',
          tipo: 'senha',
          placeholder: 'Repita a nova senha',
          colSpan: 12,
        },
      ],
    },
  ];

  constructor(
    private readonly authService: AuthService,
    private readonly mensagemService: MensagemService,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly botaoVoltarContext: BotaoVoltarContextService,
  ) {
    this.formPerfil = this.formBuilder.group({
      nome: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
    });

    this.formSenha = this.formBuilder.group({
      senhaAtual: ['', Validators.required],
      novaSenha: ['', [Validators.required, Validators.minLength(6)]],
      confirmarSenha: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.atualizarContextoRota(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.atualizarContextoRota(event.urlAfterRedirects));

    this.menuItens = [
      {
        label: 'Meu perfil',
        icon: 'pi pi-user',
        command: () => this.abrirPerfil(false),
      },
      {
        label: 'Atualizar meus dados',
        icon: 'pi pi-user-edit',
        command: () => this.abrirPerfil(true),
      },
      {
        label: 'Alterar senha',
        icon: 'pi pi-lock',
        command: () => this.abrirAlterarSenha(),
      },
      {
        separator: true,
      },
      {
        label: 'Sair',
        icon: 'pi pi-sign-out',
        command: () => this.sair(),
      },
    ];
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  protected get usuario() {
    return this.authService.usuario();
  }

  protected get sessaoRestante(): string | null {
    const restanteMs = this.authService.sessaoRestanteMs();
    if (restanteMs === null || restanteMs === undefined) {
      return null;
    }
    const totalSegundos = Math.max(0, Math.floor(restanteMs / 1000));
    const minutos = Math.floor(totalSegundos / 60);
    const segundos = totalSegundos % 60;
    const horas = Math.floor(minutos / 60);
    return `${String(horas).padStart(2, '0')}:${String(minutos % 60).padStart(2, '0')}:${String(segundos).padStart(2, '0')}`;
  }

  protected get sessaoCritica(): boolean {
    const restanteMs = this.authService.sessaoRestanteMs();
    if (restanteMs === null || restanteMs === undefined) {
      return false;
    }
    return restanteMs <= 5 * 60 * 1000;
  }

  protected get iniciaisUsuario(): string {
    const nome = this.usuario?.nome?.trim();
    if (!nome) {
      return 'U';
    }
    const partes = nome.split(' ').filter(Boolean);
    if (partes.length === 1) {
      return partes[0].slice(0, 2).toUpperCase();
    }
    return `${partes[0][0]}${partes[1][0]}`.toUpperCase();
  }

  protected abrirPerfil(editavel: boolean): void {
    const usuario = this.usuario;
    if (!usuario) {
      return;
    }
    this.formPerfil.patchValue({ nome: usuario.nome, email: usuario.email });
    if (editavel) {
      this.formPerfil.enable({ emitEvent: false });
    } else {
      this.formPerfil.disable({ emitEvent: false });
    }
    this.dialogPerfilAberto = true;
  }

  protected abrirAlterarSenha(): void {
    this.formSenha.reset();
    this.dialogSenhaAberto = true;
  }

  protected salvarPerfil(): void {
    if (this.formPerfil.invalid) {
      this.formPerfil.markAllAsTouched();
      return;
    }
    this.carregandoPerfil = true;
    this.authService.atualizarMe(this.formPerfil.getRawValue()).subscribe({
      next: (resposta) => {
        if (resposta.data) {
          this.authService.refreshUsuario(resposta.data);
        }
        this.mensagemService.sucesso('Dados atualizados com sucesso.');
        this.dialogPerfilAberto = false;
        this.carregandoPerfil = false;
      },
      error: () => {
        this.mensagemService.erro('Não foi possível atualizar os dados.');
        this.carregandoPerfil = false;
      },
    });
  }

  protected salvarSenha(): void {
    if (this.formSenha.invalid) {
      this.formSenha.markAllAsTouched();
      return;
    }
    const novaSenha = this.formSenha.get('novaSenha')?.value;
    const confirmar = this.formSenha.get('confirmarSenha')?.value;
    if (novaSenha !== confirmar) {
      this.formSenha.get('confirmarSenha')?.setErrors({ senhaDiferente: true });
      return;
    }

    this.carregandoSenha = true;
    this.authService.alterarSenha(this.formSenha.getRawValue()).subscribe({
      next: () => {
        this.mensagemService.sucesso('Senha alterada com sucesso.');
        this.dialogSenhaAberto = false;
        this.carregandoSenha = false;
      },
      error: () => {
        this.mensagemService.erro('Não foi possível alterar a senha.');
        this.carregandoSenha = false;
      },
    });
  }

  protected sair(): void {
    this.authService.logout();
  }

  protected voltarParaListagem(): void {
    if (this.botaoVoltarContext.solicitarVoltar()) {
      return;
    }

    void this.router.navigateByUrl(this.urlListagemAtual());
  }

  private atualizarContextoRota(url: string): void {
    const urlSemFragmento = url.split('#')[0];
    const [path] = urlSemFragmento.split('?');
    const segmentos = path.split('/').filter(Boolean);

    this.ehRotaCadastroDetalhe.set(this.verificarRotaDetalhe(segmentos));
    this.urlListagemAtual.set(this.resolverUrlListagem(segmentos));
  }

  private verificarRotaDetalhe(segmentos: string[]): boolean {
    if (!segmentos.length) {
      return false;
    }

    const ultimo = segmentos[segmentos.length - 1];
    const penultimo = segmentos[segmentos.length - 2];

    return (
      ultimo === 'novo' ||
      ultimo === 'nova' ||
      ['editar', 'visualizar', 'analisar'].includes(ultimo) ||
      (!!penultimo && /^\d+$/.test(ultimo) && ['diario-obra', 'medicoes'].includes(penultimo))
    );
  }

  private resolverUrlListagem(segmentos: string[]): string {
    if (!segmentos.length) {
      return '/';
    }

    const ultimo = segmentos[segmentos.length - 1];
    const penultimo = segmentos[segmentos.length - 2];

    if (ultimo === 'novo' || ultimo === 'nova') {
      return `/${segmentos.slice(0, -1).join('/')}`;
    }

    if ((ultimo === 'editar' || ultimo === 'visualizar' || ultimo === 'analisar') && penultimo) {
      return `/${segmentos.slice(0, -2).join('/')}`;
    }

    if (/^\d+$/.test(ultimo) && segmentos.length > 1) {
      return `/${segmentos.slice(0, -1).join('/')}`;
    }

    return `/${segmentos.join('/')}`;
  }
}
